import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ArticleIcon from '@mui/icons-material/Article';
import PaletteIcon from '@mui/icons-material/Palette';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import SyncIcon from '@mui/icons-material/Sync';
import TableChartIcon from '@mui/icons-material/TableChart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
    setUnsavedDialogOpener, runUnsavedSaves, hasAnySaver, currentUnsavedItems,
} from '../library/UnsavedChangesGuard';

// SoftTouch palette (mirrors library/Colors.js — kept local so the dialog
// can paint precise warm-amber + sft-blue accents the MUI theme doesn't expose).
const SFT_BLUE = '#0071BB';
const AMBER    = '#E8920C';
const RED      = '#D9534F';

// Map an item's `icon` key to the SAME icon its admin tab uses, so each
// unsaved row is recognisable at a glance.
const SECTION_ICONS = {
    pages:      ArticleIcon,
    style:      PaletteIcon,
    features:   InfoOutlinedIcon,
    processing: ShoppingBasketIcon,
    sync:       SyncIcon,
    data:       TableChartIcon,
    stores:     StorefrontIcon,
};

/**
 * Host for the unsaved-changes confirmation. Mounted ONCE in App; the
 * UnsavedChangesGuard calls it (via setUnsavedDialogOpener) whenever an
 * exit point needs the user's decision. Shows the list of sections that
 * hold unsaved edits so the choice is an informed one.
 *
 * Resolves the guard's promise with true (leave anyway) / false (stay).
 */
const UnsavedChangesDialog = ({ t, theme }) => {
    const tr = (key, fb) => (typeof t === 'function' ? t(key, fb) : fb);
    const [open, setOpen]     = useState(false);
    const [items, setItems]   = useState([]);
    const [saving, setSaving] = useState(false);
    // Whether to offer "Save & continue". Computed when the dialog OPENS (not
    // at mount) — at mount no screen is loaded yet, so no save fn is registered.
    const [canSave, setCanSave] = useState(false);
    const resolverRef = useRef(null);

    useEffect(() => {
        setUnsavedDialogOpener((unsavedItems) => new Promise((resolve) => {
            // A second request while one is pending answers the first with
            // "stay" so we never leak an unresolved promise.
            if (resolverRef.current) resolverRef.current(false);
            resolverRef.current = resolve;
            setItems(unsavedItems || []);
            setCanSave(hasAnySaver());
            setSaving(false);
            setOpen(true);
        }));
        return () => setUnsavedDialogOpener(null);
    }, []);

    const answer = (leave) => {
        setOpen(false);
        const resolve = resolverRef.current;
        resolverRef.current = null;
        if (resolve) resolve(leave);
    };

    // Save & continue: persist every screen's pending changes, then leave —
    // but only if the save actually cleared everything. If something failed
    // (the screen shows its own error) we stay and refresh the list.
    const saveAndLeave = async () => {
        setSaving(true);
        await runUnsavedSaves();
        // Let the screens' post-save state flush so the dirty refs are fresh.
        await new Promise((r) => setTimeout(r, 80));
        const remaining = currentUnsavedItems();
        setSaving(false);
        if (remaining.length === 0) answer(true);
        else setItems(remaining);
    };

    const isDark = theme === 'dark';

    // Theme-aware surface tokens.
    const paperBg     = isDark ? '#222b37' : '#ffffff';
    const headTint    = isDark
        ? 'linear-gradient(180deg, rgba(232,146,12,0.14) 0%, rgba(232,146,12,0) 100%)'
        : 'linear-gradient(180deg, rgba(232,146,12,0.10) 0%, rgba(232,146,12,0) 100%)';
    const textMain    = isDark ? '#F9FAFB' : '#1F2937';
    const textMuted   = isDark ? 'rgba(249,250,251,0.62)' : 'rgba(31,41,55,0.60)';
    const itemBg      = isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,113,187,0.045)';
    const itemBorder  = isDark ? 'rgba(255,255,255,0.08)'  : 'rgba(0,0,0,0.06)';
    const ringBg      = isDark ? 'rgba(232,146,12,0.18)'   : 'rgba(232,146,12,0.13)';
    const hairline    = isDark ? 'rgba(255,255,255,0.08)'  : 'rgba(0,0,0,0.07)';

    const count = items.length;
    const countWord = count === 1
        ? tr('unsaved_changes_count_one', 'onderdeel')
        : tr('unsaved_changes_count_many', 'onderdelen');

    return (
        // NB: never put className={theme} on <Dialog> — it lands on the
        // viewport-filling MuiModal-root and the global `.light`/`.dark`
        // background rule then paints an opaque sheet over the whole page.
        // Theme the Paper instead.
        <Dialog
            open={open}
            onClose={() => !saving && answer(false)}
            maxWidth="xs"
            fullWidth
            slotProps={{
                paper: {
                    className: theme,
                    sx: {
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 3.5,
                        backgroundColor: paperBg,
                        backgroundImage: 'none',
                        border: `1px solid ${hairline}`,
                        boxShadow: isDark
                            ? '0 24px 60px -12px rgba(0,0,0,0.7)'
                            : '0 24px 60px -16px rgba(16,24,40,0.28)',
                        // The whole panel eases up + in over the MUI backdrop fade.
                        '@keyframes sftDialogIn': {
                            from: { opacity: 0, transform: 'translateY(10px) scale(0.985)' },
                            to:   { opacity: 1, transform: 'translateY(0) scale(1)' },
                        },
                        animation: 'sftDialogIn 260ms cubic-bezier(0.22, 1, 0.36, 1)',
                    },
                },
                backdrop: {
                    sx: {
                        backgroundColor: isDark ? 'rgba(8,11,16,0.62)' : 'rgba(16,24,40,0.42)',
                        backdropFilter: 'blur(3px)',
                    },
                },
            }}
        >
            {/* Top accent rail — amber "heads up", not red "error". */}
            <Box sx={{ height: 4, background: `linear-gradient(90deg, ${AMBER} 0%, #F5B544 100%)` }} />

            {/* Close affordance, top-right. */}
            <IconButton
                onClick={() => answer(false)}
                disabled={saving}
                aria-label={tr('cancel', 'Annuleer')}
                sx={{
                    position: 'absolute', top: 10, right: 10, zIndex: 1,
                    color: textMuted,
                    '&:hover': { color: textMain, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                }}
                size="small"
            >
                <CloseRoundedIcon fontSize="small" />
            </IconButton>

            {/* Header — icon ring with a soft radial glow + title + count pill. */}
            <Box sx={{
                px: 3, pt: 3, pb: 2,
                background: headTint,
                display: 'flex', gap: 1.75, alignItems: 'flex-start',
            }}>
                <Box sx={{
                    flexShrink: 0,
                    width: 46, height: 46, borderRadius: '14px',
                    display: 'grid', placeItems: 'center',
                    backgroundColor: ringBg,
                    color: AMBER,
                    boxShadow: `0 0 0 1px ${isDark ? 'rgba(232,146,12,0.30)' : 'rgba(232,146,12,0.22)'} inset`,
                    '@keyframes sftIconPop': {
                        '0%':   { transform: 'scale(0.6)', opacity: 0 },
                        '60%':  { transform: 'scale(1.08)' },
                        '100%': { transform: 'scale(1)', opacity: 1 },
                    },
                    animation: 'sftIconPop 360ms cubic-bezier(0.22, 1, 0.36, 1) 60ms both',
                }}>
                    <ReportProblemRoundedIcon />
                </Box>
                <Box sx={{ minWidth: 0, pt: 0.25 }}>
                    <Typography sx={{
                        fontWeight: 800, fontSize: '1.12rem', lineHeight: 1.25,
                        color: textMain, letterSpacing: '-0.01em',
                    }}>
                        {tr('unsaved_changes_title', 'Onbewaarde wijzigingen')}
                    </Typography>
                    {count > 0 && (
                        <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            mt: 0.6, px: 1, py: 0.2, borderRadius: 999,
                            backgroundColor: ringBg, color: AMBER,
                            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.01em',
                        }}>
                            {count} {countWord}
                        </Box>
                    )}
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, pt: 0.5, pb: 1.5 }}>
                <Typography sx={{ fontSize: '0.875rem', color: textMuted, lineHeight: 1.5, mb: count > 0 ? 1.75 : 0 }}>
                    {tr('unsaved_changes_intro', 'De volgende onderdelen hebben wijzigingen die nog niet zijn opgeslagen en verloren gaan als je doorgaat:')}
                </Typography>

                {count > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {items.map((item, i) => {
                            // Back-compat: a plain string still renders (no tab/icon).
                            const obj     = (item && typeof item === 'object') ? item : { section: String(item) };
                            const tabName = obj.tab || '';
                            const section = obj.section || obj.tab || '';
                            const Icon    = SECTION_ICONS[obj.icon] || EditOutlinedIcon;
                            return (
                                <Box
                                    key={`${tabName}-${section}-${i}`}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.25,
                                        px: 1.25, py: 0.9,
                                        borderRadius: 2,
                                        backgroundColor: itemBg,
                                        border: `1px solid ${itemBorder}`,
                                        '@keyframes sftItemIn': {
                                            from: { opacity: 0, transform: 'translateY(6px)' },
                                            to:   { opacity: 1, transform: 'translateY(0)' },
                                        },
                                        animation: `sftItemIn 320ms cubic-bezier(0.22, 1, 0.36, 1) ${100 + i * 45}ms both`,
                                    }}
                                >
                                    <Box sx={{
                                        flexShrink: 0,
                                        width: 30, height: 30, borderRadius: '9px',
                                        display: 'grid', placeItems: 'center',
                                        backgroundColor: isDark ? 'rgba(0,113,187,0.22)' : 'rgba(0,113,187,0.10)',
                                        color: SFT_BLUE,
                                    }}>
                                        <Icon sx={{ fontSize: 17 }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        {tabName && obj.section ? (
                                            <Typography sx={{
                                                fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.04em',
                                                textTransform: 'uppercase', color: textMuted, lineHeight: 1.2,
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                                {tabName}
                                            </Typography>
                                        ) : null}
                                        <Typography sx={{
                                            fontSize: '0.85rem', fontWeight: 600, color: textMain, lineHeight: 1.3,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {section}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {!canSave && (
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mt: 2 }}>
                        <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: 2, backgroundColor: AMBER, opacity: 0.5, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.78rem', color: textMuted, lineHeight: 1.45 }}>
                            {tr('unsaved_changes_hint', 'Tip: klik op Annuleer en sla eerst op via de Opslaan-knop.')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {/* Stacked full-width actions — three labels don't fit side by side
                in an xs dialog. Order = most → least prominent, top to bottom. */}
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5, flexDirection: 'column', alignItems: 'stretch', gap: 1, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
                {/* Save & continue — the recommended action (only when a screen
                    actually exposes a save fn). */}
                {canSave && (
                    <Button
                        onClick={saveAndLeave}
                        variant="contained"
                        disableElevation
                        autoFocus
                        disabled={saving}
                        fullWidth
                        startIcon={saving
                            ? <CircularProgress size={15} thickness={5} sx={{ color: '#fff' }} />
                            : <SaveRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, fontSize: '0.85rem',
                            py: 1, borderRadius: 2,
                            backgroundColor: SFT_BLUE,
                            boxShadow: '0 6px 16px -6px rgba(0,113,187,0.6)',
                            '&:hover': { backgroundColor: '#005c99', boxShadow: '0 8px 20px -6px rgba(0,113,187,0.7)' },
                        }}
                    >
                        {saving
                            ? tr('unsaved_changes_saving', 'Bezig met opslaan…')
                            : tr('unsaved_changes_save_leave', 'Opslaan & doorgaan')}
                    </Button>
                )}
                {/* Stay — secondary. */}
                <Button
                    onClick={() => answer(false)}
                    disabled={saving}
                    fullWidth
                    sx={{
                        textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', py: 0.9, borderRadius: 2,
                        color: SFT_BLUE,
                        border: `1px solid ${hairline}`,
                        '&:hover': { backgroundColor: 'rgba(0,113,187,0.06)', borderColor: SFT_BLUE },
                    }}
                >
                    {tr('cancel', 'Annuleer')}
                </Button>
                {/* Discard — least prominent (red text, bottom). */}
                <Button
                    onClick={() => answer(true)}
                    disabled={saving}
                    disableElevation
                    fullWidth
                    sx={{
                        textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                        color: RED,
                        '&:hover': { backgroundColor: 'rgba(217,83,79,0.08)' },
                    }}
                >
                    {tr('unsaved_changes_leave', 'Doorgaan zonder opslaan')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

UnsavedChangesDialog.propTypes = {
    t:     PropTypes.func,
    theme: PropTypes.string,
};

export default UnsavedChangesDialog;
